import {
    FunctionComponent,
    memo,
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { DataBadge } from '@/components/Statistics/DataBadge';
import {
    Box,
    Divider,
    Grid,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import UsersIcon from '@heroicons/react/24/solid/UsersIcon';
import { BarDiagramm } from '@/components/Statistics/BarDiagramm';
import { StatisticType } from '@/models/statistic';
import { StarjackTable } from '@/components/Table/v2/Table';
import { useLazyGetAggregatedTicketsQuery } from '@/api/v2/liftOperator';
import { AggregatedTicket, getSumMoney } from '@/models/aggregatedTicket';
import { getTicketTypeString } from '@/utils/translations';
import { getMoneyAsString } from '@/utils';
import { usePagination } from '@/hooks/usePagination';
import { addUpCosts } from '@/utils/money';
import { formatToShortString } from '@/utils/number';
import { StatisticsFilter } from '@/pageContents/statistics/StatisticsFilter';

const envs = [
    { value: 'starjack', label: 'STARJACK.AT / STARJACK APP / Webshop' },
    { value: 'hospitality', label: 'HOSPITALITY' },
];

const getStatisticTypeByEnv = (
    env: 'starjack' | 'hospitality'
): StatisticType[] =>
    env === 'starjack'
        ? [StatisticType.PORTAL, StatisticType.INTEGRATION]
        : [StatisticType.HOSPITALITY];

export const StatisticsContent: FunctionComponent = () => {
    const theme = useTheme();
    const [
        getAggregatedTicket,
        { data: aggregatedTickets = [], isUninitialized, isFetching },
    ] = useLazyGetAggregatedTicketsQuery();
    const [showLoadingState, setShowLoadingState] = useState(true);

    const [starjackEnvironment, setStarjackEnvironment] = useState<
        'starjack' | 'hospitality'
    >('starjack');
    const [rowsPerPage, setRowsPerPage] = useState(5);
    const { currentItems, setNextPage } = usePagination({
        items: aggregatedTickets,
        itemsPerPage: rowsPerPage,
    });

    const sums = useMemo(
        () => ({
            sales: formatToShortString(
                addUpCosts(getSumMoney(aggregatedTickets, 'sumSales'))
            ),
            cancellations: formatToShortString(
                addUpCosts(getSumMoney(aggregatedTickets, 'sumCancellations'))
            ),
            deposits: formatToShortString(
                addUpCosts(getSumMoney(aggregatedTickets, 'sumDepositAmounts'))
            ),
            cancelledDeposits: formatToShortString(
                addUpCosts(
                    getSumMoney(aggregatedTickets, 'sumCancelledDepositAmounts')
                )
            ),
        }),
        [aggregatedTickets]
    );

    const handleChange = useCallback((e) => {
        const { value } = e.target;

        setStarjackEnvironment(value);
    }, []);

    useEffect(() => {
        const loading = isFetching || isUninitialized;

        if (loading) {
            setShowLoadingState(loading);
        } else {
            setTimeout(() => {
                setShowLoadingState(false);
            }, 800);
        }
    }, [isFetching, isUninitialized]);

    useEffect(() => {
        getAggregatedTicket({
            statisticTypes: getStatisticTypeByEnv(starjackEnvironment),
        });
    }, [starjackEnvironment]);

    if (isUninitialized) return null;

    return (
        <Stack spacing={theme.page.verticalSpacing}>
            <TextField
                sx={{
                    maxWidth: 400,
                }}
                fullWidth
                label='Starjack Umgebung auswählen'
                required
                select
                onChange={handleChange}
                SelectProps={{ native: true }}
                value={starjackEnvironment}
            >
                {envs.map(({ value, label }) => (
                    <option key={value} value={value}>
                        {label}
                    </option>
                ))}
            </TextField>

            <Divider
                sx={{
                    my: 4,
                    ml: 3,
                    width: '100%',
                }}
            />

            <StatisticsFilter
                loading={showLoadingState}
                onSearch={(params) => {
                    getAggregatedTicket({
                        statisticTypes:
                            getStatisticTypeByEnv(starjackEnvironment),
                        ...params,
                    });
                }}
            />

            <Grid
                sx={(theme) => ({
                    ml: `-${theme.spacing(3)} !important`, // correct margin left px error
                })}
                spacing={3}
                container
            >
                <Grid md={3} item>
                    <DataBadge
                        title='Verkäufe in EUR'
                        type='difference'
                        avatarColor={theme.palette.primary.main}
                        avatarIcon={<UsersIcon />}
                        data={{
                            value: sums.sales,
                            positive: true,
                            difference: 16,
                        }}
                        loading={showLoadingState}
                    />
                </Grid>

                <Grid md={3} item>
                    <DataBadge
                        title='Storno in EUR'
                        type='difference'
                        avatarColor={theme.palette.error.main}
                        avatarIcon={<UsersIcon />}
                        data={{
                            value: sums.cancellations,
                            positive: false,
                            difference: 16,
                        }}
                        loading={showLoadingState}
                    />
                </Grid>

                <Grid md={3} item>
                    <DataBadge
                        title='Pfand in EUR'
                        type='difference'
                        avatarColor={theme.palette.success.main}
                        avatarIcon={<UsersIcon />}
                        data={{
                            value: sums.deposits,
                            positive: true,
                            difference: 16,
                        }}
                        loading={showLoadingState}
                    />
                </Grid>

                <Grid md={3} item>
                    <DataBadge
                        title='Pfand storniert in EUR'
                        type='plain'
                        avatarColor={theme.palette.secondary.main}
                        avatarIcon={<UsersIcon />}
                        data={{
                            value: sums.cancelledDeposits,
                        }}
                        loading={showLoadingState}
                    />
                </Grid>
            </Grid>

            <BarDiagramm
                chartSeries={[
                    {
                        name: 'This year',
                        data: [18, 16, 5, 8, 3, 14, 14, 16, 17, 19, 18, 20],
                    },
                    {
                        name: 'Last year',
                        data: [12, 11, 4, 6, 2, 9, 9, 10, 11, 12, 13, 13],
                    },
                ]}
            />

            <StarjackTable<AggregatedTicket>
                count={aggregatedTickets.length}
                items={currentItems}
                itemId={'name'}
                columns={[
                    {
                        label: 'Ticketname',
                        itemTransform: (ticket) => (
                            <TicketName ticket={ticket} />
                        ),
                    },
                    {
                        label: 'Ticketart',
                        itemTransform: (ticket) =>
                            getTicketTypeString(ticket.liftOperatorTicketType),
                    },
                    {
                        label: 'Personentyp',
                        itemKey: 'personName',
                    },
                    {
                        label: 'Preis Verkauf',
                        itemTransform: (ticket) =>
                            getMoneyAsString(ticket.sumSales),
                    },

                    {
                        label: 'Preis storno',
                        itemTransform: (ticket) =>
                            getMoneyAsString(ticket.sumCancellations),
                    },
                ]}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(nextValue) => setRowsPerPage(nextValue)}
                onPageChange={(nextValue) => setNextPage(nextValue)}
                rowsSelectable
                loading={showLoadingState}
            />
        </Stack>
    );
};

const TicketName: FunctionComponent<{ ticket: AggregatedTicket }> = memo(
    ({ ticket }) => (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'center',
                flexDirection: 'column',
            }}
        >
            <Typography
                style={{
                    fontSize: '0.875rem',
                    lineHeight: '1.43',
                    letterSpacing: '0.01071em',
                }}
                gutterBottom
            >
                {ticket.name}
            </Typography>

            {!!ticket.conditions.length && (
                <Typography
                    style={{
                        lineHeight: '1.43',
                        letterSpacing: '0.01071em',
                    }}
                    sx={{
                        ml: '4px !important',
                        fontSize: '0.85rem !important',
                    }}
                >
                    {ticket.conditions.map(
                        (c, index) =>
                            `${c}${
                                index < ticket.conditions.length - 1 ? ', ' : ''
                            }`
                    )}
                </Typography>
            )}
        </Box>
    )
);

TicketName.displayName = 'TicketName';
